import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';

export interface LinkItem {
  id: string;
  title: string;
  url: string;
  icon?: string;
  isActive: boolean;
}

interface LinksContextType {
  links: LinkItem[];
  addLink: (link: LinkItem) => void;
  updateLink: (id: string, link: Partial<LinkItem>) => void;
  removeLink: (id: string) => void;
  reorderLinks: (newLinks: LinkItem[]) => void;
}

const LinksContext = createContext<LinksContextType | undefined>(undefined);

const defaultLinks: LinkItem[] = [
  { id: '1', title: 'Site Oficial', url: 'https://literafan.com', isActive: true },
  { id: '2', title: 'YouTube', url: 'https://www.youtube.com/@literafan', isActive: true },
  { id: '3', title: 'Instagram', url: 'https://www.instagram.com/literafan?igsh=eDhxejBkbDZlb2ty', isActive: true },
  { id: '4', title: 'TikTok', url: 'https://www.tiktok.com/@literafan_?_t=ZM-8timJV3t3J7&_r=1', isActive: true },
  { id: '5', title: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61554102537888&rdid=14ggXPA5AwGqZWMo&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F1F1XqTLiSh%2F#', isActive: true },
  { id: '6', title: 'X (Twitter)', url: 'https://x.com/literafan_', isActive: true },
  { id: '7', title: 'Twitch', url: 'https://www.twitch.tv/literafan_', isActive: true },
  { id: '8', title: 'Kick', url: 'https://kick.com/literafan', isActive: true },
  { id: '9', title: 'Discord', url: 'https://discord.gg/dQrr92QpM', isActive: true }
];

export const LinksProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [links, setLinks] = useState<LinkItem[]>([]);

  useEffect(() => {
    const storedLinks = localStorage.getItem('literafan_social_links');
    if (storedLinks) {
      try {
        let parsed: LinkItem[] = JSON.parse(storedLinks);
        // Find if discord exists
        const discordIndex = parsed.findIndex(l => l.url.includes('discord.gg') || l.title.toLowerCase().includes('discord'));
        let discordItem: LinkItem;
        if (discordIndex !== -1) {
          discordItem = parsed.splice(discordIndex, 1)[0];
        } else {
          discordItem = { id: 'discord-link', title: 'Discord', url: 'https://discord.gg/dQrr92QpM', isActive: true };
        }

        // Find kick index and insert discord right after kick
        const kickIndex = parsed.findIndex(l => l.url.includes('kick.com') || l.title.toLowerCase().includes('kick'));
        if (kickIndex !== -1) {
          parsed.splice(kickIndex + 1, 0, discordItem);
        } else {
          parsed.push(discordItem);
        }

        setLinks(parsed);
        localStorage.setItem('literafan_social_links', JSON.stringify(parsed));
      } catch {
        setLinks(defaultLinks);
      }
    } else {
      setLinks(defaultLinks);
    }
  }, []);

  const addLink = (link: LinkItem) => {
    const newLinks = [...links, link];
    setLinks(newLinks);
    localStorage.setItem('literafan_social_links', JSON.stringify(newLinks));
  };

  const updateLink = (id: string, updatedFields: Partial<LinkItem>) => {
    const newLinks = links.map(link => link.id === id ? { ...link, ...updatedFields } : link);
    setLinks(newLinks);
    localStorage.setItem('literafan_social_links', JSON.stringify(newLinks));
  };

  const removeLink = (id: string) => {
    const newLinks = links.filter(link => link.id !== id);
    setLinks(newLinks);
    localStorage.setItem('literafan_social_links', JSON.stringify(newLinks));
  };

  const reorderLinks = (newLinks: LinkItem[]) => {
    setLinks(newLinks);
    localStorage.setItem('literafan_social_links', JSON.stringify(newLinks));
  };

  return (
    <LinksContext.Provider value={{ links, addLink, updateLink, removeLink, reorderLinks }}>
      {children}
    </LinksContext.Provider>
  );
};

export const useLinks = () => {
  const context = useContext(LinksContext);
  if (context === undefined) {
    throw new Error('useLinks must be used within a LinksProvider');
  }
  return context;
};
